import { TICK_HZ } from '../const.js';
import type { Modifier } from '../modifier.js';

/**
 * DER WAHRSAGER — the Zoltar in the corner (machines.md / catalogue.md, wonder machines).
 *
 * "Sells prophecies that are **self-fulfilling modifiers**: 'The next jackpot will be a
 * curse'." The joke and the mechanic are the same thing: a prophecy is not a prediction,
 * buying it is what *makes* it true. Each entry below is an ordinary grammar `Modifier`
 * whose rule text happens to be written as a fortune, and each one had to answer the same
 * design question before it shipped: **what build wants this?** A prophecy you can only
 * suffer is a tax wearing a costume.
 *
 * They cost TICKETS, never coins — economy.md: "coins pay debts, tickets buy futures", and
 * a prophecy is the most literal future in the game.
 *
 * **Built per run, never module-level.** This is a factory, and it is a factory on
 * purpose. Two of these prophecies need to remember something across a session ("the
 * *next* jackpot"), and a remembering modifier that lives at module scope is exactly the
 * trap MASCHINENBAU documented and paid for: run 2 inherits run 1's latch and twin-run
 * determinism dies silently. STATUS.md records Montagsmaschine and Die Dauerkarte as
 * unbuilt for precisely this reason, with "the director should build them per run" as the
 * known-correct fix. This is that fix, written down and working.
 *
 * The covenant still holds: this is `core/items/`, not `core/machines/`. No cabinet knows
 * a prophecy exists; these speak the same query hooks every charm speaks.
 */

export interface Prophecy {
  id: string;
  /** what the machine prints on its little card */
  name: string;
  /** the fortune, as spoken. German, dry, and true by construction. */
  text: string;
  /** in TICKETS */
  price: number;
  modifier: Modifier;
}

function prophecy(
  id: string,
  name: string,
  text: string,
  price: number,
  queries: Modifier['queries'],
  events?: Modifier['events'],
): Prophecy {
  return {
    id, name, text, price,
    modifier: {
      // A bought prophecy is a charm: it is yours, it is printed on the Hausregeln board,
      // and it is not a hidden thumb on the scale.
      id: `weissagung:${id}`,
      source: 'charm',
      tags: [],
      // late in the resolution order: a prophecy is meant to have the last word
      priority: 620,
      ruleText: text,
      discovered: true,
      queries,
      events,
    },
  };
}

/** Fresh prophecies, with fresh memories. One call per run. */
export function makeProphecies(): Prophecy[] {
  return [
    /**
     * The catalogue's own headline example, and it is genuinely self-fulfilling: the
     * prophecy does nothing at all until you hit a jackpot, and hitting one is what turns
     * it into a curse. For eight seconds afterwards your material stage pays nothing —
     * and you are five GLÜCK richer.
     *
     * Who wants it: the [Fluch] tank, which farms GLÜCK off losses anyway and is happy to
     * trade one jackpot for the hoard that steers the Saalchef. How to dodge it: cash out
     * the moment the jackpot lands and let the dead window burn while you walk.
     */
    (() => {
      let armed = true;
      let cursedUntil = -1;
      return prophecy(
        'fluch-jackpot',
        'Der nächste Jackpot wird ein Fluch',
        'Der nächste Jackpot verflucht Ihr Material für acht Sekunden — und schenkt Ihnen fünf GLÜCK.',
        12,
        {
          materialMult: (c, v) => (c.tick < cursedUntil ? 0 : v),
        },
        {
          onScore: (ctx, info) => {
            if (!armed || !info.jackpot) return;
            armed = false;
            cursedUntil = ctx.tick + TICK_HZ * 8;
            ctx.api.glueck(5);
            ctx.api.verdacht(-8, 'Die Weissagung hat sich erfüllt');
          },
        },
      );
    })(),

    /**
     * A prophecy about *material*, which is really a deckbuilding instruction: it tells
     * you which half of your bag to play tonight. Copper runs hot, silver goes cold, so
     * the cheap tokens you never stake suddenly outperform the ones you were saving.
     */
    prophecy(
      'kupfernacht',
      'Eine kupferne Nacht',
      'Kupfer zahlt heute anderthalbfach. Silber wird Sie enttäuschen.',
      10,
      {
        materialMult: (c, v) => {
          const m = c.token?.material;
          if (m === 'kupfer') return v * 1.5;
          if (m === 'silber') return v * 0.6;
          return v;
        },
      },
    ),

    /**
     * The pot turns against you and the mouths that feed it stand wider. A TOPF build
     * hates it; a near-miss build is delighted, because it fills faster and the pot was
     * only ever a waypoint on the road to bursting the machine.
     */
    prophecy(
      'enttaeuschter-topf',
      'Der Topf wird Sie enttäuschen',
      'Der Topf zahlt nur die Hälfte — dafür stehen alle Taschenmäuler ein Viertel weiter offen.',
      10,
      {
        stageValue: (c, v) => (c.stage === 'topf' ? v * 0.5 : v),
        pocketWidth: (_c, v) => v * 1.25,
      },
    ),

    /**
     * The self-fulfilling one about breaking things. Cabinets burst a third sooner, and
     * the House takes it personally. That is the whole Bank-sprengen build in one card:
     * overload a machine deliberately, cash the burst, wear the heat.
     */
    prophecy(
      'sie-werden-brechen',
      'Sie werden brechen, was Ihnen gehört',
      'Automaten bersten ein Drittel früher — und der Saal sieht es Ihnen an.',
      14,
      {
        limitScale: (_c, v) => v * 1.35,
      },
      {
        onBankBreak: (ctx) => {
          ctx.api.verdacht(15, 'Die Weissagung hat sich erfüllt');
        },
      },
    ),

    /**
     * The streak doubles. The fortune warns you it will break, which is not a new rule —
     * a dud has always reset STRÄHNE to one — it is simply true, said out loud, while
     * making the streak worth twice as much. Deadpan is the whole house style.
     */
    prophecy(
      'straehne-reisst',
      'Ihre Strähne wird reißen',
      'Die STRÄHNE zählt doppelt. Sie wissen selbst, wie schnell eine Strähne reißt.',
      12,
      {
        stageValue: (c, v) => (c.stage === 'straehne' ? v * 2 : v),
      },
    ),
  ];
}
